import React, { useEffect, useState } from "react";
import { Member, MemberInquiry } from "../../lib/types/member";
import AdminService from "../../services/AdminService";
import { MemberStatus, MemberType } from "../../lib/enums/member.enum";
import moment from "moment";
import { Job, JobInquiry, JobUpdateInput } from "../../lib/types/job";
import { JobStatus } from "../../lib/enums/jobs.enum";
import { useGlobals } from "../../hooks/useGlobals";
import { useNavigate } from "react-router";

const AdminPage = () => {
  const { authMember } = useGlobals();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>({ totalMembers: 0, totalJobs: 0 });
  const [members, setMembers] = useState<Member[]>([]);
  const [totalMembers, setTotalMembers] = useState<number>(0);
  const [interviews, setInterviews] = useState<Job[]>([]);
  const [totalInterviews, setTotalInterviews] = useState<number>(0);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [inquiry, setInquiry] = useState<MemberInquiry>({ page: 1, limit: 5 });
  const [jobInquiry, setJobInquiry] = useState<JobInquiry>({
    page: 1,
    limit: 5,
  });
  const [activeTab, setActiveTab] = useState<"users" | "interviews" | "stats">(
    "users"
  );

  useEffect(() => {
    getStatsByAdmin();
  }, []);

  useEffect(() => {
    if (!authMember) {
      navigate("/");
      return;
    }

    if (authMember.memberType !== MemberType.ADMIN) {
      navigate("/");
      return;
    }
  }, [authMember, navigate]);

  useEffect(() => {
    console.log(activeTab);
    if (activeTab === "users") {
      getMembersByAdmin();
    }
  }, [inquiry, activeTab]);

  useEffect(() => {
    if (activeTab === "interviews") {
      getJobsByAdmin();
    }
  }, [jobInquiry, activeTab]);

  const getStatsByAdmin = async () => {
    try {
      const adminService = new AdminService();
      const data = await adminService.getStatsByAdmin();

      setStats(data);
    } catch (err) {
      console.log(err);
    }
  };

  const getMembersByAdmin = async () => {
    try {
      const adminService = new AdminService();
      const { data, totalCount } = await adminService.getMembersByAdmin(
        inquiry
      );
      setMembers(data);
      setTotalMembers(totalCount);
    } catch (err) {
      console.log(err);
    }
  };

  const updateMemberByAdmin = async (
    memberId: string,
    status: MemberStatus
  ) => {
    try {
      const adminService = new AdminService();
      await adminService.updateMemberByAdmin(memberId, status);
      setMembers((prevMembers) =>
        prevMembers.map((member) =>
          member._id === memberId ? { ...member, memberStatus: status } : member
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const getJobsByAdmin = async () => {
    try {
      const adminService = new AdminService();
      const { data, totalCount } = await adminService.getJobsByAdmin(
        jobInquiry
      );
      setInterviews(data);
      setTotalInterviews(totalCount);
    } catch (err) {
      console.log(err);
    }
  };

  const updateJobByAdmin = async (jobId: string, input: JobUpdateInput) => {
    try {
      const adminService = new AdminService();
      await adminService.updateJobByAdmin(jobId, input);
      setInterviews((prev) =>
        prev.map((interview: Job) =>
          interview._id === jobId
            ? { ...interview, status: input.jobStatus! }
            : interview
        )
      );
    } catch (err) {
      console.log(err);
    }
  };

  const getJobByAdmin = async (jobId: string) => {
    try {
      const adminService = new AdminService();
      const data = await adminService.getJobByAdmin(jobId);
      setSelectedJob(data);
      setShowModal(true);
    } catch (err) {
      console.log(err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handlePageChange = (newPage: number) => {
    setInquiry({ ...inquiry, page: newPage });
  };

  const handleJobPageChange = (newPage: number) => {
    setJobInquiry({ ...jobInquiry, page: newPage });
  };

  const totalPages = Math.ceil(totalMembers / inquiry.limit);
  const totalJobPages = Math.ceil(totalInterviews / jobInquiry.limit);

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, inquiry.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6 px-4">
        <div className="text-sm text-gray-600">
          Showing {(inquiry.page - 1) * inquiry.limit + 1} to{" "}
          {Math.min(inquiry.page * inquiry.limit, totalMembers)} of{" "}
          {totalMembers} users
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(inquiry.page - 1)}
            disabled={inquiry.page === 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              inquiry.page === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 transform hover:scale-105"
            }`}
          >
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white transition-all duration-200"
              >
                1
              </button>
              {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
            </>
          )}

          {pages.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                inquiry.page === page
                  ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg transform scale-105"
                  : "bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white"
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-2 text-gray-500">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white transition-all duration-200"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(inquiry.page + 1)}
            disabled={inquiry.page === totalPages}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              inquiry.page === totalPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 transform hover:scale-105"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderJobPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      jobInquiry.page - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(totalJobPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6 px-4">
        <div className="text-sm text-gray-600">
          Showing {(jobInquiry.page - 1) * jobInquiry.limit + 1} to{" "}
          {Math.min(jobInquiry.page * jobInquiry.limit, totalInterviews)} of{" "}
          {totalInterviews} interviews
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleJobPageChange(jobInquiry.page - 1)}
            disabled={jobInquiry.page === 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              jobInquiry.page === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 transform hover:scale-105"
            }`}
          >
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handleJobPageChange(1)}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white transition-all duration-200"
              >
                1
              </button>
              {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
            </>
          )}

          {pages.map((page) => (
            <button
              key={page}
              onClick={() => handleJobPageChange(page)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                jobInquiry.page === page
                  ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg transform scale-105"
                  : "bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white"
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < totalJobPages && (
            <>
              {endPage < totalJobPages - 1 && (
                <span className="px-2 text-gray-500">...</span>
              )}
              <button
                onClick={() => handleJobPageChange(totalJobPages)}
                className="px-4 py-2 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-purple-500 hover:to-blue-500 hover:text-white transition-all duration-200"
              >
                {totalJobPages}
              </button>
            </>
          )}

          <button
            onClick={() => handleJobPageChange(jobInquiry.page + 1)}
            disabled={jobInquiry.page === totalJobPages}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
              jobInquiry.page === totalJobPages
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 transform hover:scale-105"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-xl border-r border-gray-200">
        <div className="p-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Admin Panel
          </h2>
          <p className="text-sm text-gray-500 mt-1">Dashboard Management</p>
        </div>

        <nav className="mt-6">
          {/* <button
            className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 ${
              activeTab === "stats"
                ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                : "text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("stats")}
          >
            <span className="text-2xl">📊</span>
            <div>
              <div className="font-semibold">Statistics</div>
              <div
                className={`text-xs ${
                  activeTab === "stats" ? "text-purple-100" : "text-gray-500"
                }`}
              >
                Overview & Analytics
              </div>
            </div>
          </button> */}

          <button
            className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 ${
              activeTab === "users"
                ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                : "text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("users")}
          >
            <span className="text-2xl">👥</span>
            <div>
              <div className="font-semibold">Users</div>
              <div
                className={`text-xs ${
                  activeTab === "users" ? "text-purple-100" : "text-gray-500"
                }`}
              >
                Manage all users
              </div>
            </div>
          </button>

          <button
            className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 ${
              activeTab === "interviews"
                ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                : "text-gray-700 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("interviews")}
          >
            <span className="text-2xl">📝</span>
            <div>
              <div className="font-semibold">Interviews</div>
              <div
                className={`text-xs ${
                  activeTab === "interviews"
                    ? "text-purple-100"
                    : "text-gray-500"
                }`}
              >
                Interview records
              </div>
            </div>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="max-w-9xl mx-auto">
          {/* Header */}
          {/* <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {activeTab === "stats" && "Dashboard Overview"}
              {activeTab === "users" && "User Management"}
              {activeTab === "interviews" && "Interview Management"}
            </h1>
            <p className="text-gray-600">
              {activeTab === "stats" &&
                "View your platform statistics and analytics"}
              {activeTab === "users" && "Manage and monitor all platform users"}
              {activeTab === "interviews" && "Track and manage all interviews"}
            </p>
          </div> */}

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Statistics Tab */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl p-6 text-white transform hover:scale-105 transition-transform duration-300 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="text-5xl">👥</div>
                  <div>
                    <h3 className="text-4xl font-bold">{stats.totalMembers}</h3>
                    <p className="text-purple-100 mt-2 font-medium">
                      Total Users
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-pink-500 to-red-500 rounded-2xl p-6 text-white transform hover:scale-105 transition-transform duration-300 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="text-5xl">📝</div>
                  <div>
                    <h3 className="text-4xl font-bold">{stats.totalJobs}</h3>
                    <p className="text-pink-100 mt-2 font-medium">Interviews</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Tab */}
            {activeTab === "users" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      All Users
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage user accounts and permissions
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full min-w-max">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Member
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Auth
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Username
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Email
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Join
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Status
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Block
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {members.map((member) => (
                        <tr
                          key={member._id}
                          className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200"
                        >
                          <td className="px-4 py-5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                                {member.memberImage ? (
                                  <img
                                    src={
                                      member.memberImage ||
                                      "/default-avatar.png"
                                    }
                                    alt={member.memberName}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-cyan-500"
                                  />
                                ) : (
                                  member.memberName.charAt(0)
                                )}
                              </div>
                              <span
                                className="font-semibold text-gray-900 text-xs max-w-[200px] truncate"
                                title={member._id}
                              >
                                {member.memberName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              {member.memberAuthType}
                            </span>
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            {member.memberUserName ?? "-"}
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            {member.memberEmail}
                          </td>
                          <td className="px-4 py-5 text-gray-600 font-medium text-sm whitespace-nowrap">
                            {moment(member.createdAt).format(
                              "YYYY-MM-DD HH:mm:ss"
                            )}
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold ${
                                member.memberStatus === MemberStatus.ACTIVE
                                  ? "bg-gradient-to-r from-green-100 to-teal-100 text-green-700"
                                  : "bg-gradient-to-r from-red-100 to-orange-100 text-red-700"
                              }`}
                            >
                              {member.memberStatus}
                            </span>
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            {member.memberStatus === MemberStatus.DELETE ? (
                              "-"
                            ) : (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={
                                    member.memberStatus === MemberStatus.BLOCK
                                  }
                                  onChange={(e: any) =>
                                    updateMemberByAdmin(
                                      member._id,
                                      e.target.checked
                                        ? MemberStatus.BLOCK
                                        : MemberStatus.ACTIVE
                                    )
                                  }
                                />
                                <div className="w-11 h-6 bg-gradient-to-r from-green-400 to-green-500 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-red-400 peer-checked:to-red-500"></div>
                              </label>
                            )}
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            <div className="flex gap-2">
                              <button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105">
                                Edit
                              </button>
                              <button className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </div>
            )}

            {/* Interviews Tab */}
            {activeTab === "interviews" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      All Interviews
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Track and manage interview records
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full min-w-max">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Member
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Type
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Platform
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Language
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Date
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Status
                        </th>
                        <th className="px-4 py-4 text-left text-sm font-bold text-gray-700 whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {interviews.map((interview: Job) => (
                        <tr
                          key={interview._id}
                          className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-200"
                        >
                          <td className="px-4 py-5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-red-400 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                                {interview.member?.memberImage ? (
                                  <img
                                    src={interview.member.memberImage}
                                    alt={interview.member.memberName}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-red-500"
                                  />
                                ) : (
                                  interview.member?.memberName.charAt(0) || "U"
                                )}
                              </div>
                              <span className="font-semibold text-gray-900 text-sm">
                                {interview.member?.memberName || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                              {interview.type}
                            </span>
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-700">
                              {interview.platform}
                            </span>
                          </td>
                          <td className="px-4 py-5 text-gray-600 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                              {interview.language}
                            </span>
                          </td>
                          <td className="px-4 py-5 text-gray-600 font-medium text-sm whitespace-nowrap">
                            {moment(interview.createdAt).format(
                              "YYYY-MM-DD HH:mm"
                            )}
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-teal-100 text-green-700`}
                            >
                              {interview.status}
                            </span>
                          </td>
                          <td className="px-4 py-5 whitespace-nowrap">
                            <div className="flex gap-2">
                              <button
                                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105"
                                onClick={() => getJobByAdmin(interview._id)}
                              >
                                View
                              </button>
                              {interview.status === JobStatus.DELETE ? (
                                <button
                                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105"
                                  onClick={() =>
                                    updateJobByAdmin(interview._id, {
                                      jobStatus: JobStatus.ACTIVE,
                                    })
                                  }
                                >
                                  ACTIVE
                                </button>
                              ) : (
                                <button
                                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105"
                                  onClick={() =>
                                    updateJobByAdmin(interview._id, {
                                      jobStatus: JobStatus.DELETE,
                                    })
                                  }
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {renderJobPagination()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Job Details */}
      {showModal && selectedJob && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    {selectedJob.member?.memberImage ? (
                      <img
                        src={selectedJob.member.memberImage}
                        alt={selectedJob.member.memberName}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-purple-600">
                        {selectedJob.member?.memberName.charAt(0) || "U"}
                      </span>
                    )}
                  </div>
                  <div className="text-white">
                    <h3 className="text-2xl font-bold">
                      {selectedJob.member?.memberName || "Unknown User"}
                    </h3>
                    <p className="text-purple-100 text-sm">
                      {selectedJob.member?.memberEmail}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Interview Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-purple-600 uppercase mb-1">
                    Type
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedJob.type}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-cyan-600 uppercase mb-1">
                    Platform
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedJob.platform}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-indigo-600 uppercase mb-1">
                    Language
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedJob.language}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-pink-600 uppercase mb-1">
                    Status
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedJob.status}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs font-semibold text-gray-600 uppercase mb-2">
                  Description
                </p>
                <p className="text-gray-800 leading-relaxed">
                  {selectedJob.description}
                </p>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-blue-600 uppercase mb-1">
                    Created At
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {moment(selectedJob.createdAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                  <p className="text-xs font-semibold text-green-600 uppercase mb-1">
                    Updated At
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {moment(selectedJob.updatedAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )}
                  </p>
                </div>
              </div>

              {/* Evaluations */}
              {selectedJob.evaluations &&
                selectedJob.evaluations.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">📊</span>
                      Evaluations
                      <span className="text-sm font-normal text-gray-500">
                        ({selectedJob.evaluations.length} total)
                      </span>
                    </h4>
                    <div className="space-y-4">
                      {selectedJob.evaluations.map(
                        (evaluation: any, index: number) => (
                          <div
                            key={index}
                            className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl hover:shadow-md transition-all duration-200 border border-gray-200"
                          >
                            {/* Question */}
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-purple-600 uppercase mb-1">
                                Question #{index + 1}
                              </p>
                              <p className="text-gray-800 font-medium">
                                {evaluation.question}
                              </p>
                            </div>

                            {/* Answer */}
                            {evaluation.answer && (
                              <div className="mb-3 bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <p className="text-xs font-semibold text-blue-600 uppercase mb-1">
                                  User's Answer
                                </p>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {evaluation.answer}
                                </p>
                              </div>
                            )}

                            {/* Feedback */}
                            {evaluation.feedback && (
                              <div className="mb-3 bg-green-50 p-3 rounded-lg border border-green-200">
                                <p className="text-xs font-semibold text-green-600 uppercase mb-1">
                                  AI Feedback
                                </p>
                                <p className="text-gray-700 text-sm leading-relaxed">
                                  {evaluation.feedback}
                                </p>
                              </div>
                            )}

                            {/* Score */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                              <span className="font-semibold text-gray-800">
                                Score
                              </span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-green-400 to-cyan-400 rounded-full transition-all duration-500"
                                    style={{
                                      width: `${evaluation.score || 0}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="text-lg font-bold text-gray-900 min-w-[50px]">
                                  {evaluation.score || 0}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
